import { CarvanaHeaderProvider } from '@carvana/header';
import type React from 'react';
import { PortalContextProvider } from '@/Portal/PortalContext';
import {
  AppliedFiltersProvider,
  type OwnProps as AppliedFiltersContextProps
} from '~/contexts/AppliedFiltersContext';
import {
  ConditionalTileContextProvider,
  type OwnProps as ConditionalTileContextProps
} from '~/contexts/ConditionalTileContext';
import {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/contexts/DeviceContext';
import { ErrorContextProvider } from '~/contexts/ErrorContext';
import {
  FacetDataContextProvider,
  type OwnProps as FacetDataContextProps
} from '~/contexts/FacetDataContext';
import { FavoritesContextProvider } from '~/contexts/FavoritesContext';
import {
  FinanceAndTradeContextProvider,
  type FinanceAndTradeContextProps
} from '~/contexts/FinanceAndTradeContext';
import {
  InventoryContextProvider,
  type OwnProps as InventoryContextProps
} from '~/contexts/InventoryContext';
import { ModalContextProvider } from '~/contexts/ModalContext';
import { PricingContextProvider } from '~/contexts/PricingContext';
import {
  RouteContextProvider,
  type OwnProps as RouteContextProps
} from '~/contexts/RouteContext';
import { SavedSearchesContextProvider } from '~/contexts/SavedSearchesContext';
import {
  SearchContextProvider,
  type OwnProps as SearchContextProps
} from '~/contexts/SearchContext';
import {
  SearchEventsDataContextProvider,
  type OwnProps as SearchEventDataContextProps
} from '~/contexts/SearchEventsDataContext';
import {
  SEOContextProvider,
  type OwnProps as SEOContextProps
} from '~/contexts/SEOContext';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from '~/contexts/StaticContext';
import { SuggestionsContextProvider } from '~/contexts/SuggestionsContext';
import {
  UserContextProvider,
  type OwnProps as UserContextProps
} from '~/contexts/UserContext';
import AnalyticsProvider from './AnalyticsProvider';
import ExperimentProvider, {
  type OwnProps as ExperimentContextProps
} from './ExperimentProvider';

export type OwnProps = React.PropsWithChildren<{
  forAppliedFiltersContext: AppliedFiltersContextProps;
  forConditionalTileContext: ConditionalTileContextProps;
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forFacetDataContext: FacetDataContextProps;
  forFinanceAndTradeContext: FinanceAndTradeContextProps;
  forInventoryContext: InventoryContextProps;
  forRouteContext: RouteContextProps;
  forSearchContext: SearchContextProps;
  forSearchEventDataContext: SearchEventDataContextProps;
  forSEOContext: SEOContextProps;
  forStaticContext: StaticContextProps;
  forUserContext: UserContextProps;
  requestUrl?: string;
}>;

export default function SearchProviders({
  children,
  forAppliedFiltersContext,
  forConditionalTileContext,
  forDeviceContext,
  forExperimentContext,
  forFacetDataContext,
  forFinanceAndTradeContext,
  forInventoryContext,
  forRouteContext,
  forSearchContext,
  forSearchEventDataContext,
  forSEOContext,
  forStaticContext,
  forUserContext
}: OwnProps) {
  return (
    <StaticContextProvider {...forStaticContext}>
      <ExperimentProvider {...forExperimentContext}>
        <CarvanaHeaderProvider>
          <AnalyticsProvider project="search" track="search">
            <DeviceContextProvider {...forDeviceContext}>
              <UserContextProvider {...forUserContext}>
                <SearchContextProvider {...forSearchContext}>
                  <PricingContextProvider>
                    <RouteContextProvider {...forRouteContext}>
                      <SEOContextProvider {...forSEOContext}>
                        <ConditionalTileContextProvider
                          {...forConditionalTileContext}
                        >
                          <FacetDataContextProvider {...forFacetDataContext}>
                            <ErrorContextProvider>
                              <InventoryContextProvider
                                {...forInventoryContext}
                              >
                                <FinanceAndTradeContextProvider
                                  {...forFinanceAndTradeContext}
                                >
                                  <AppliedFiltersProvider
                                    {...forAppliedFiltersContext}
                                  >
                                    <SearchEventsDataContextProvider
                                      {...forSearchEventDataContext}
                                    >
                                      <SuggestionsContextProvider>
                                        <FavoritesContextProvider>
                                          <PortalContextProvider>
                                            <ModalContextProvider>
                                              <SavedSearchesContextProvider>
                                                {children}
                                              </SavedSearchesContextProvider>
                                            </ModalContextProvider>
                                          </PortalContextProvider>
                                        </FavoritesContextProvider>
                                      </SuggestionsContextProvider>
                                    </SearchEventsDataContextProvider>
                                  </AppliedFiltersProvider>
                                </FinanceAndTradeContextProvider>
                              </InventoryContextProvider>
                            </ErrorContextProvider>
                          </FacetDataContextProvider>
                        </ConditionalTileContextProvider>
                      </SEOContextProvider>
                    </RouteContextProvider>
                  </PricingContextProvider>
                </SearchContextProvider>
              </UserContextProvider>
            </DeviceContextProvider>
          </AnalyticsProvider>
        </CarvanaHeaderProvider>
      </ExperimentProvider>
    </StaticContextProvider>
  );
}
